// Unpublished Work © 2021-2025 Deere & Company.

import * as actions from '../../action-list';
import {Customer, customerKeys} from '../../state/struct/customer';
import {Address} from '../../state/struct/customer/address';
import {Communication} from '../../state/struct/customer/communication';
import {Phone} from '../../state/struct/customer/phone';
import {addressType} from '../../state/enum/address-type';
import {GovernmentTaxId} from '../../state/struct/customer/government-tax-id';
import {
    businessNameFieldKeys,
    contactPhoneFieldKeys,
    individualNameFieldKeys
} from '../../state/struct/add-customer/field-keys';

const updateCustomer = (state, formKey, newValue) =>
    Customer.update(state, {
        [formKey]: {
            $set: newValue
        }
    });

const updateCustomerCommunication = (state, formKey, newValue) =>
    Customer.update(state, {
        communication: {
            $merge: {
                [formKey]: newValue
            }
        }
    });

const setCustomerForm = (state, action) => Customer.update(state, {
    $merge: {
        ...action.customer
    }
});

const adaptAddress = (addressFromRegistry, addressFromAddForm) => ({
    ...addressFromRegistry,
    addressComponent: addressFromAddForm.addressComponent
});

const persistAddressComponentForCustomer = (originalCustomer, customerFromRegistry) => ({
    ...customerFromRegistry,
    providedPrivacyPolicy: true,
    communication: {
        ...customerFromRegistry.communication,
        physicalAddress: adaptAddress(customerFromRegistry.communication.physicalAddress, originalCustomer.communication.physicalAddress),
        mailingAddress: adaptAddress(customerFromRegistry.communication.mailingAddress, originalCustomer.communication.mailingAddress)
    }
});

const setPotentialMatchCustomerInfo = (state, action) => Customer(persistAddressComponentForCustomer(state, action.customer));

const setCustomerType = (state, action) => updateCustomer(state, customerKeys.CUSTOMER_TYPE, action.customerType);

const setProvidedPrivacyPolicy = (state, action) => updateCustomer(state, customerKeys.PRIVACY_POLICY, action.isProvided);

const setEmailNotAvailableCheck = (state, action) =>
    Customer.update(state, {
        communication: {
            email: {
                emailNotAvailableCheckSelected: {
                    $set: action.isEmailNotAvailable
                }
            }
        }
    });

const setMiddleNameCheckBoxNotAvailableCheck = (state, action) =>
    Customer.update(state, {
        customerName: {
            middleNameNotAvailableCheckSelected: {
                $set: action.isMiddleNameNotAvailable
            }
        }
    });

const setEmailAddress = (state, action) =>
    Customer.update(state, {
        communication: {
            email: {
                emailAddress: {
                    $set: action.value
                }
            }
        }
    });

const setAllEmailAddress = (state, action) =>
    Customer.update(state, {
        communication: {
            email: {
                $set: action.value
            }
        }
    });

const setContactInfoPhone = (state, action) =>
    Customer.update(state, {
        communication: {
            [action.fieldKey]: {
                [action.contactPhoneFieldKey]: {
                    $set: action.value
                }
            }
        }
    });

const setContactInfoPhoneAll = (state, action) =>
    Customer.update(state, {
        communication: {
            [action.fieldKey]: {
                [contactPhoneFieldKeys.AREA_CODE]: {
                    $set: action.value[contactPhoneFieldKeys.AREA_CODE]
                },
                [contactPhoneFieldKeys.EXTENSION]: {
                    $set: action.value[contactPhoneFieldKeys.EXTENSION]
                },
                [contactPhoneFieldKeys.PHONE]: {
                    $set: action.value[contactPhoneFieldKeys.PHONE]
                },
                [contactPhoneFieldKeys.COUNTRY_CODE]: {
                    $set: action.value[contactPhoneFieldKeys.COUNTRY_CODE]
                },
                [contactPhoneFieldKeys.FORMATTED_PHONE]: {
                    $set: action.value[contactPhoneFieldKeys.FORMATTED_PHONE]
                }
            }
        }
    });

const setPreferredLanguage = (state, action) =>
    Customer.update(state, {
        communication: {
            preferredLanguage: {
                $set: action.preferredLanguage
            }
        }
    });

const setAsterisk = (state, action) =>
    Customer.update(state, {
        communication: {
            [action.formKey]: {
                canShowAsterisk: {
                    $set: action.showAsterisk
                }
            }
        }
    });

const setCustomerName = (state, action) => {
    const shouldMirrorValues = action.fieldKey === businessNameFieldKeys.BUSINESS_NAME_ONE || action.fieldKey === individualNameFieldKeys.LAST;

    if (shouldMirrorValues) {
        return Customer.update(state, {
            customerName: {
                [businessNameFieldKeys.BUSINESS_NAME_ONE]: {
                    $set: action.value
                },
                [individualNameFieldKeys.LAST]: {
                    $set: action.value
                }
            }
        });
    }

    return Customer.update(state, {
        customerName: {
            [action.fieldKey]: {
                $set: action.value
            }
        }
    });
};

const setCustomerDateOfBirth = (state, action) =>
    Customer.update(state, {
        birthDate: {
            $set: action.value
        }
    });

const setSsnDetails = (state, action) =>
    Customer.update(state, {
        ssn: {
            $set: action.values
        }
    });

const setUsCtzInd = (state, action) =>
    Customer.update(state, {
        usCtznInd: {
            $set: action.value
        }
    });

const setBusinessTaxIdDetails = (state, action) =>
    Customer.update(state, {
        taxId: {
            $set: action.value
        }
    });

const setFilingDetails = (state, action) =>
    Customer.update(state, {
        filingDetails: {
            $merge: action.values
        }
    });

const setCustomerNameValues = (state, action) =>
    Customer.update(state, {
        customerName: {
            $merge: action.values
        }
    });

const setPrefilledDetails = (state, action) => Customer.update(state, {
    customerName: {
        $merge: action.preFilledCustomerDetails.customerName
    },
    communication: {
        $merge: action.preFilledCustomerDetails.communication
    },
    [customerKeys.CUSTOMER_TYPE]: {
        $set: action.preFilledCustomerDetails.customerType
    }
});

const updateGovernmentTaxId = (newTaxIdInfo, indexOfTaxId, state) => indexOfTaxId >= 0 ? Customer.update(state, {
    governmentTaxIds: {
        [indexOfTaxId]: {
            $set: newTaxIdInfo
        }
    }
}) : Customer.update(state, {
    governmentTaxIds: {
        $push: [newTaxIdInfo]
    }
});

const getIndexOfTaxId = (govTaxIds, taxIdRow) => govTaxIds.findIndex((govRow) => govRow.countryCode === taxIdRow.countryCode && govRow.taxIdType === taxIdRow.taxIdType);

const setCustomerTaxIdNotAvailable = (state, action) => {
    const indexOfTaxId = getIndexOfTaxId(state.governmentTaxIds, action.taxIdRow);

    const newTaxId = GovernmentTaxId({
        countryCode: action.taxIdRow.countryCode,
        taxIdType: action.taxIdRow.taxIdType,
        taxId: action.newValue
    });

    return updateGovernmentTaxId(newTaxId, indexOfTaxId, state);
};

const setCustomerGovernmentTaxId = (state, action) => {
    const indexOfTaxId = getIndexOfTaxId(state.governmentTaxIds, action.taxIdRow);

    const newTaxId = GovernmentTaxId({
        countryCode: action.taxIdRow.countryCode,
        taxIdType: action.taxIdRow.taxIdType,
        taxId: action.newTaxIdValue
    });

    return updateGovernmentTaxId(newTaxId, indexOfTaxId, state);
};

const setCustomerGovernmentTaxIdToDefault = (state) => Customer.update(state, {
    governmentTaxIds: {
        $set: []
    }
});

const setCustomerTaxId = (state, action) => {
    const hasTaxId = state.governmentTaxIds.length;

    if (hasTaxId) {
        return Customer.update(state, {
            governmentTaxIds: {
                '0': {
                    taxId: {
                        $set: action.taxId
                    }
                }
            }
        });
    }

    const newTaxId = GovernmentTaxId({
        countryCode: action.countryCode,
        taxId: action.taxId,
        taxIdType: action.taxIdType
    });

    return Customer.update(state, {
        governmentTaxIds: {
            $push: [newTaxId]
        }
    });
};

function getUpdatedGovernmentTaxIds(state, action, newTaxId, index) {
    if (index >= 0) {
        return Customer.update(state, {
            governmentTaxIds: {
                [index]: {
                    $set: newTaxId
                }
            }
        });
    }
    return Customer.update(state, {
        governmentTaxIds: {
            $push: [newTaxId]
        }
    });
}

const setRegistrantNumber = (state, action) => {
    const newTaxId = GovernmentTaxId({
        countryCode: action.countryCode,
        taxId: action.taxId,
        taxIdType: action.taxIdType
    });
    const index = state.governmentTaxIds.findIndex(
        (row) => row.countryCode === action.countryCode && row.taxIdType === action.taxIdType
    );

    return getUpdatedGovernmentTaxIds(state, action, newTaxId, index);
};

const setPhysicalAddress = (state, action) =>
    Customer.update(state, {
        communication: {
            physicalAddress: {
                [action.fieldKey]: {
                    $set: action.value
                }
            }
        }
    });

const copyPhysicalToMailingAddress = (state) => {
    const {
        type, ...physicalAddress
    } = state.communication.physicalAddress;

    return Customer.update(state, {
        communication: {
            mailingAddress: {
                $set: {
                    ...state.communication.mailingAddress,
                    ...physicalAddress
                }
            }
        }
    });
};

const setMailingAddress = (state, action) =>
    Customer.update(state, {
        communication: {
            mailingAddress: {
                [action.fieldKey]: {
                    $set: action.value
                }
            }
        }
    });

const setCitiesDropdownList = (state, action) => Customer.update(state, {
    communication: {
        [action.addressType]: {
            addressComponent: {
                cities: {
                    $set: action.cities
                }
            }
        }
    }
});

const resetCustomer = (state, action) => {
    const {
        addressComponent,
        selectedValues
    } = action.domainData;

    const defaultPhone = new Phone({
        countryCode: selectedValues.country
    });

    const updated = Customer({
        communication: Communication({
            workFax: defaultPhone,
            workPhone: defaultPhone,
            homeFax: defaultPhone,
            homePhone: defaultPhone,
            mobilePhone: defaultPhone,
            mailingAddress: Address({
                addressComponent,
                countryCode: selectedValues.country,
                type: addressType.MAILING
            }),
            physicalAddress: Address({
                addressComponent,
                countryCode: selectedValues.country,
                type: addressType.PHYSICAL
            })
        })
    });

    return Customer.update(state, {
        $set: updated
    });
};

const resetContactPhone = (state, action) =>
    Customer.update(state, {
        communication: {
            [action.fieldKey]: {
                $set: new Phone({
                    countryCode: action.defaultCountry
                })
            }
        }
    });

const resetAddress = (state, action) => updateCustomerCommunication(state, action.formKey, action.payload);

export const customer = (state = Customer({}), action = {}) => {
    const actionHandlers = {
        [actions.RESET_ADD_CUSTOMER_CONTACT_PHONE]: resetContactPhone,
        [actions.RESET_ADD_CUSTOMER_FORM]: resetCustomer,
        [actions.RESET_ADDRESS]: resetAddress,
        [actions.SET_ADD_CUSTOMER]: setCustomerForm,
        [actions.SET_POTENTIAL_MATCH_CUSTOMER_INFO]: setPotentialMatchCustomerInfo,
        [actions.SET_ADD_CUSTOMER_EMAIL_INFO]: setEmailAddress,
        [actions.SET_ADD_ALL_CUSTOMER_EMAIL_INFO]: setAllEmailAddress,
        [actions.SET_ADD_CUSTOMER_CONTACT_PHONE]: setContactInfoPhone,
        [actions.SET_ADD_CUSTOMER_CONTACT_PHONE_ALL]: setContactInfoPhoneAll,
        [actions.SET_ADD_CUSTOMER_TYPE]: setCustomerType,
        [actions.SET_ASTERISK]: setAsterisk,
        [actions.SET_CUSTOMER_NAME_DETAILS]: setCustomerName,
        [actions.SET_CUSTOMER_DATE_OF_BIRTH]: setCustomerDateOfBirth,
        [actions.SET_SSN_DETAILS]: setSsnDetails,
        [actions.SET_REGISTRANT_NUMBER_DETAILS]: setRegistrantNumber,
        [actions.SET_US_CTZ_IND]: setUsCtzInd,
        [actions.SET_CUSTOMER_PREFIX_AND_GENERATION_DETAILS]: setCustomerNameValues,
        [actions.SET_TAX_ID_DETAILS]: setCustomerTaxId,
        [actions.SET_BUSINESS_TAX_DETAILS]: setBusinessTaxIdDetails,
        [actions.SET_CUSTOMER_FILING_DETAILS]: setFilingDetails,
        [actions.SET_GOVERNMENT_TAX_ID_DETAILS]: setCustomerGovernmentTaxId,
        [actions.SET_GOVERNMENT_TAX_ID_TO_DEFAULT]: setCustomerGovernmentTaxIdToDefault,
        [actions.SET_CITY_ADD_EDIT_DROPDOWN]: setCitiesDropdownList,
        [actions.SET_TAX_ID_NOT_AVAILABLE]: setCustomerTaxIdNotAvailable,
        [actions.SET_EDIT_CUSTOMER]: setCustomerForm,
        [actions.SET_EMAIL_NOT_AVAILABLE_CHECK]: setEmailNotAvailableCheck,
        [actions.SET_MIDDLE_NAME_NOT_AVAILABLE_CHECK]: setMiddleNameCheckBoxNotAvailableCheck,
        [actions.SET_MAILING_ADDRESS]: setMailingAddress,
        [actions.SET_PHYSICAL_ADDRESS]: setPhysicalAddress,
        [actions.COPY_PHYSICAL_TO_MAILING_ADDRESS]: copyPhysicalToMailingAddress,
        [actions.SET_PROVIDED_PRIVACY_POLICY]: setProvidedPrivacyPolicy,
        [actions.SET_PREFERRED_LANGUAGE]: setPreferredLanguage,
        [actions.SET_PREFILLED_CUSTOMER_DETAILS]: setPrefilledDetails,
        [actions.SET_CUSTOMER_DETAILS_FROM_FACETS]: setPrefilledDetails
    };
    const reducer = actionHandlers[action.type];

    return reducer ? reducer(state, action) : state;
};
